import { getTheme, SpinnerSize } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPIconButton, IPLabel, IPLink, IPSpinner, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { allowedMimeTypesForUpload } from 'globalConstants';
import { deleteIcon, globalStackTokensGapExtraSmall, globalStackTokensGapSmall } from 'globalStyles';
import { DragEvent, useContext, useEffect, useState } from 'react';
import Dropzone, { DropEvent, FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { bytesToSize } from 'utils/string';

// Add type for file with ID
type UploadFile = { id: string; file: File };

interface IFilePickerProps {
  label?: string;
  onSelect: (items: File[]) => void;
  onChangeName: (item: File, newName: string | undefined, idx: number) => void;
  onRemove: (item: File) => void;
  onFileTypeError: () => void;
  selectedFiles: UploadFile[];
  selectedFileNames: Record<string, string | undefined>;
  fileNameErrors: Record<string, string | undefined>;
  actionPending: boolean;
  pendingFileId?: string;
  finishedFileIds?: string[];
  grow?: boolean;
  fillAvailableHeight?: boolean;
  fillDropzoneWhenEmpty?: boolean;
  preventUnhandledDrop?: boolean;
}

const FilePicker = (props: IFilePickerProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation']);
  const [files, setFiles] = useState<UploadFile[]>(props.selectedFiles || []);
  const [dragOverTextField, setDragOverTextField] = useState<boolean>(false);
  const [editingFileNames, setEditingFileNames] = useState<string[]>([]);
  const [itemToFocus, setItemToFocus] = useState<string | null>(null);

  useEffect(() => {
    const editingFileNamesIds: string[] = [...editingFileNames];

    Object.keys(props.fileNameErrors).forEach((key) => {
      if (props.fileNameErrors[key] && !editingFileNamesIds.includes(key)) {
        editingFileNamesIds.push(key);
      }
    });

    setEditingFileNames(editingFileNamesIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.fileNameErrors]);

  useEffect(() => {
    setFiles(props.selectedFiles || []);
  }, [props.selectedFiles]);

  const theme = getTheme();
  const dropzoneHeight = props.grow === true ? 130 : 80;
  const textFieldHeight = props.grow === true ? 110 : 60;
  const fillDropzone = props.fillDropzoneWhenEmpty === true && files.length === 0;

  const selectFiles = (newFiles: File[]) => {
    // This function is now handled in parent, so just call onSelect
    props.onSelect(newFiles);
  };

  const removeFile = (file: File) => {
    props.onRemove(file);
  };

  const preventUnhandledDrop = (event: DragEvent<HTMLDivElement>) => {
    if (props.preventUnhandledDrop === true) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // Only stop editing the name if there is no error. Otherwise, keep the text field open.
  const stopEditingFileName = (uploadFile: UploadFile) => {
    if (editingFileNames.includes(uploadFile.id) && !props.fileNameErrors[uploadFile.id]) {
      setEditingFileNames(editingFileNames.filter((i) => i !== uploadFile.id));
    }
  };

  return (
    <IPStack
      verticalFill
      styles={props.fillAvailableHeight === true ? { root: { height: '100%', minHeight: 0 } } : undefined}
      onDragOver={preventUnhandledDrop}
      onDrop={preventUnhandledDrop}
    >
      {props.label && (
        <IPStack.Item>
          <IPLabel>{props.label}</IPLabel>
        </IPStack.Item>
      )}
      {files.length > 0 && (
        <IPStack.Item
          grow
          styles={{
            root: {
              minHeight: props.fillAvailableHeight === true ? 0 : undefined,
              maxHeight: props.fillAvailableHeight === true ? undefined : props.grow === true ? 600 : 200,
              flexBasis: props.fillAvailableHeight === true ? 0 : undefined,
              overflowY: 'auto',
              border: '1px solid grey',
              borderColor: appContext.useDarkMode ? theme.palette.themeLight : theme.palette.themeDark,
              backgroundColor: appContext.useDarkMode ? theme.palette.themeDark : theme.palette.themeLighter,
              padding: 10,
              marginBottom: globalStackTokensGapSmall.childrenGap,
            },
          }}
        >
          {files.map((uploadFile, idx) => (
            <IPStack key={uploadFile.id} horizontal verticalAlign="center" tokens={globalStackTokensGapExtraSmall}>
              <IPStack.Item grow style={{ minHeight: 20 }}>
                {editingFileNames.includes(uploadFile.id) && !props.actionPending ? (
                  <IPTextField
                    autoFocus={itemToFocus === uploadFile.id}
                    required
                    maxLength={512}
                    readOnly={props.actionPending}
                    defaultValue={props.selectedFileNames[uploadFile.id] ?? uploadFile.file.name}
                    onChange={(_, newValue) => props.onChangeName(uploadFile.file, newValue, idx)}
                    errorMessage={props.fileNameErrors[uploadFile.id]}
                    onFocus={() => {
                      if (itemToFocus === uploadFile.id) setItemToFocus(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        stopEditingFileName(uploadFile);
                      }
                    }}
                    onBlur={() => stopEditingFileName(uploadFile)}
                    onSubmit={() => stopEditingFileName(uploadFile)}
                  />
                ) : props.actionPending ? (
                  <IPStack horizontal tokens={globalStackTokensGapSmall}>
                    <IPStack.Item
                      style={{
                        minWidth: 24,
                        textAlign: 'center',
                        fontSize: 14,
                        verticalAlign: 'bottom',
                        color: theme.palette.themePrimary,
                      }}
                      align="center"
                    >
                      {props.pendingFileId === uploadFile.id ? (
                        <IPSpinner size={SpinnerSize.xSmall} />
                      ) : (
                        props.finishedFileIds?.includes(uploadFile.id) && <IPFontIcon iconName="CheckMark" />
                      )}
                    </IPStack.Item>
                    <IPStack.Item>
                      <IPText>{props.selectedFileNames[uploadFile.id] ?? uploadFile.file.name}</IPText>
                    </IPStack.Item>
                  </IPStack>
                ) : (
                  <IPLink
                    disabled={props.actionPending}
                    onClick={(event) => {
                      event.preventDefault();
                      setItemToFocus(uploadFile.id);
                      setEditingFileNames([...editingFileNames, uploadFile.id]);
                    }}
                  >
                    <IPFontIcon
                      iconName="Edit"
                      style={{
                        fontSize: 16,
                        verticalAlign: 'bottom',
                        marginRight: 10,
                        textDecoration: 'none',
                      }}
                    />
                    {props.selectedFileNames[uploadFile.id] ?? uploadFile.file.name}
                  </IPLink>
                )}
              </IPStack.Item>
              <IPStack.Item styles={{ root: { minWidth: 70, textAlign: 'right' } }}>
                <IPText>{bytesToSize(uploadFile.file.size)}</IPText>
              </IPStack.Item>
              <IPStack.Item>
                <IPIconButton
                  disabled={props.actionPending}
                  style={props.actionPending ? { background: 'transparent' } : undefined}
                  onClick={() => removeFile(uploadFile.file)}
                  iconProps={deleteIcon}
                />
              </IPStack.Item>
            </IPStack>
          ))}
        </IPStack.Item>
      )}
      <IPStack.Item grow={fillDropzone ? 1 : undefined} styles={fillDropzone ? { root: { minHeight: 0 } } : undefined}>
        {!props.actionPending && (
          <Dropzone
            noDragEventsBubbling={true}
            multiple={true}
            maxFiles={100}
            disabled={props.actionPending}
            accept={allowedMimeTypesForUpload}
            onDrop={(acceptedFiles: File[], fileRejection: FileRejection[], event: DropEvent) => {
              setDragOverTextField(false);
              if (fileRejection.length > 0) {
                props.onFileTypeError();
              } else if (acceptedFiles.length > 0) {
                selectFiles(acceptedFiles);
              }
            }}
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <IPStack
                horizontal
                horizontalAlign="center"
                verticalAlign="center"
                styles={{
                  root: {
                    height: fillDropzone ? '100%' : undefined,
                    border:
                      isDragActive || dragOverTextField
                        ? `2px dashed ${appContext.useDarkMode ? theme.palette.themeLight : theme.palette.themeDark}`
                        : '1px dashed',
                    background:
                      isDragActive || dragOverTextField
                        ? appContext.useDarkMode
                          ? theme.palette.themeDark
                          : theme.palette.themeLight
                        : undefined,
                    minHeight: dropzoneHeight,
                    maxHeight: fillDropzone ? undefined : dropzoneHeight,
                    padding: 5,
                  },
                }}
                {...getRootProps()}
              >
                <IPStack.Item
                  styles={{ root: { width: '100%' } }}
                  onDragEnter={() => setDragOverTextField(true)}
                  onDragOver={() => setDragOverTextField(true)}
                  onDragLeave={() => setDragOverTextField(false)}
                  onDragEnd={() => setDragOverTextField(false)}
                >
                  <IPTextField
                    autoFocus={!appContext.isMobileView}
                    borderless
                    multiline
                    readOnly={true}
                    styles={{
                      fieldGroup: {
                        background:
                          isDragActive || dragOverTextField
                            ? appContext.useDarkMode
                              ? theme.palette.themeDark
                              : theme.palette.themeLight
                            : undefined,
                      },
                      field: {
                        resize: 'none',
                        overflow: 'hidden',
                        height: fillDropzone ? '100%' : textFieldHeight,
                        minHeight: textFieldHeight,
                        textAlign: 'center',
                        color: theme.palette.themePrimary,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      },
                    }}
                    defaultValue={
                      t('translation:General.FilePicker.PasteOrDrop') + t('translation:General.FilePicker.ChooseFile')
                    }
                  />
                  <IPText>
                    <input {...getInputProps({ multiple: true })} />
                  </IPText>
                </IPStack.Item>
              </IPStack>
            )}
          </Dropzone>
        )}
      </IPStack.Item>
    </IPStack>
  );
};

export default FilePicker;
